import { createSlice } from '@reduxjs/toolkit';
import {
    axiosWithTokenRefresh,
    finalizeResponse,
    handleError
} from 'tradera-utils/api';
import { toLocalizedUrl } from 'tradera-utils/url';
import ENDPOINTS from 'tradera-constants/endpoints';
import { openModal } from 'tradera-state/ui/actions';
import { INTERNATIONAL_SHIPPING_MODAL_ID } from 'tradera-components/international-shipping-modal/constants';
import {
    selectIsLoggedIn,
    selectMemberId
} from 'tradera-state/member/selectors';
import { selectPreferredLanguageCode } from 'tradera-state/language/selectors';

const internationalShippingSlice = createSlice({
    name: 'internationalShipping',
    initialState: {
        allowedBuyerRegion: null,
        fromCountryCodeIso2: null,
        imageUrl: null,
        messagingLink: null,
        ownItem: null,
        providers: [],
        rows: [],
        title: null
    },
    reducers: {
        setInternationalShipping: (state, action) => {
            state.messagingLink = action.payload.messagingLink;
            state.ownItem = action.payload.ownItem;
            const {
                imageUrl,
                title,
                shippingOptions,
                allowedBuyerRegion
            } = action.payload.data;
            state.allowedBuyerRegion = allowedBuyerRegion;
            state.imageUrl = imageUrl;
            state.title = title;
            state.fromCountryCodeIso2 =
                shippingOptions[0]?.fromCountryCodeIso2 || null;
            const { providers, rows } = shippingOptions
                .sort((a, b) =>
                    a.toCountryCodeIso2 > b.toCountryCodeIso2 ? 1 : -1
                )
                .reduce(
                    ({ providers, rows }, shippingOption) => {
                        if (
                            !providers.includes(shippingOption.shippingProvider)
                        ) {
                            providers = [
                                ...providers,
                                shippingOption.shippingProvider
                            ];
                        }
                        if (
                            rows.length === 0 ||
                            rows[rows.length - 1].toCountryCodeIso2 !==
                                shippingOption.toCountryCodeIso2
                        ) {
                            rows = [
                                ...rows,
                                {
                                    toCountryCodeIso2:
                                        shippingOption.toCountryCodeIso2
                                }
                            ];
                        }
                        rows[rows.length - 1] = {
                            ...rows[rows.length - 1],
                            [shippingOption.shippingProvider]: shippingOption
                        };
                        return { providers, rows };
                    },
                    { providers: [], rows: [] }
                );

            state.providers = providers;
            state.rows = rows;
        }
    }
});

export const fetchInternationalShipping = ({ itemId, sellerId }) => async (
    dispatch,
    getState
) => {
    const preferredLanguageCode = selectPreferredLanguageCode(getState());
    const url = toLocalizedUrl(
        `${ENDPOINTS.WEB_API}/browse/item/${itemId}/shipping`,
        preferredLanguageCode
    );
    const response = await axiosWithTokenRefresh()
        .get(url)
        .then(finalizeResponse())
        .catch(handleError());
    const data = response.data;

    data.shippingOptions = data.shippingOptions.filter(
        ({ shippingProvider }) =>
            shippingProvider !== 'Takeaway' &&
            shippingProvider !== 'TakeawayOnly'
    );

    const isLoggedIn = selectIsLoggedIn(getState());
    const buyerId = selectMemberId(getState());
    const ownItem = buyerId === sellerId;
    const messagingLink = {
        isLoggedIn,
        buyerId: buyerId?.toString(),
        memberId: buyerId?.toString(),
        sellerId: sellerId?.toString(),
        itemId: itemId?.toString(),
        source: 'contact_seller'
    };

    dispatch(
        setInternationalShipping({
            data,
            messagingLink,
            ownItem
        })
    );
    dispatch(
        openModal({
            modalId: INTERNATIONAL_SHIPPING_MODAL_ID,
            data: { itemId }
        })
    );
};

export const { setInternationalShipping } = internationalShippingSlice.actions;
export default internationalShippingSlice.reducer;
